import { Person } from '@mui/icons-material';
import { Avatar, Button, Container, Grid, List, ListItem, ListItemButton, ListItemText, TextField, Typography } from '@mui/material';
import { AppModels, AppState, CompanyAttrs, ContactAttrs, FileInfo, RelatedContactAttrs, brandNewContact, brandSlice, companyEditorSlice, companySelector, companySlice, companyValidator, contactSlice, documentSlice, generateItemCode, hasErrors, newBrand, newRelatedContact, notEmptyString, relatedContactSlice, saveModel, selectCompanyCustomersProjects, splitContactName } from 'cdx-common';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { MainViewLoader } from '../../libraries/MainViewLoader';
import { useToaster } from '../../libraries/Toaster';
import { appStore } from '../../store';
import { FileLoader } from '../AppItems';
import { MasterViewPage } from '../MasterPage';
import { SuperSearchInput } from '../SuperSearchInput';
import { RelatedContactList } from '../contacts/RelatedContactList';
import { LocationInput } from '../location/LocationInput';
import { BrandProductList } from '../product/BrandProductList';
import { AWSImageButtonUploader } from '../utils/Uploader';

export const Company = () => {
    const params = useParams();
    return <MasterViewPage>
        <MainViewLoader
            selector={companySelector}
            modelId={params.id ?? '0'}
            onModelLoaded={(company: CompanyAttrs) => {
                appStore.dispatch(documentSlice.actions.setTitle(company.name));
            }}
        >
            <LoadedCompany />
        </MainViewLoader>
    </MasterViewPage>;
};


export const LoadedCompany = () => {


    const editingCompany = useSelector((state: AppState) => state.companyEditor.attrs);
    const storedCompany = useSelector((state: AppState) => companySelector.selectById(state, editingCompany.id));
    //const brand = useSelector((state: AppState) => editingCompany.brandId ? brandSelector.selectById(state, editingCompany.brandId) : undefined);

    const projectsAsAClient = useSelector((state: AppState) => editingCompany.id ? selectCompanyCustomersProjects(state, editingCompany.id) : []);
    const [suggestCode, setSuggestCode] = React.useState('');
    const { toast } = useToaster();

    const update = (p: Partial<CompanyAttrs>) => {
        appStore.dispatch(companyEditorSlice.actions.update(p));
    };

    React.useEffect(() => {
        setSuggestCode(generateItemCode(editingCompany.name));
        console.log('editingCOmpany', editingCompany);
    }, [editingCompany]);

    const onSaveClick = async () => {
        const validatedCompany = await companyValidator.validate(editingCompany);
        if (!hasErrors(validatedCompany)) {
            appStore.dispatch(companyEditorSlice.actions.update(validatedCompany));
            const updatedCompany = await saveModel(AppModels.companies, validatedCompany);
            appStore.dispatch(companyEditorSlice.actions.update(updatedCompany));
            if (!hasErrors(updatedCompany)) {
                appStore.dispatch(companySlice.actions.updateOne({ id: updatedCompany.id, changes: updatedCompany }));
                appStore.dispatch(companyEditorSlice.actions.init(updatedCompany));
                toast.success('Modifications enregistrées');

            }
        }
    };

    const onCreateBrand = async (name: string) => {

        const response = await saveModel(AppModels.brands, {
            ...newBrand,
            name
        });
        if (!hasErrors(response)) {
            appStore.dispatch(brandSlice.actions.addOne(response));
            update({ brandId: response.id });

        }
    };

    const addContact = async (contactId: ModelId) => {
        const relatedContact: RelatedContactAttrs = {
            ...newRelatedContact,
            contactId,
            relatedType: AppModels.companies,
            relatedId: editingCompany.id,
        };

        const createdRelatedContact = await saveModel(AppModels.relatedContacts, relatedContact);
        if (!hasErrors(createdRelatedContact)) {
            appStore.dispatch(relatedContactSlice.actions.addOne(createdRelatedContact));
        }
    };

    const createContact = async (name: string) => {

        const contact: ContactAttrs = {
            ...brandNewContact,
            ...splitContactName(name)
        };
        const createdContact = await saveModel(AppModels.contacts, contact);
        if (!hasErrors(createdContact)) {
            appStore.dispatch(contactSlice.actions.addOne(createdContact));
            addContact(createdContact.id);
        }

    };


    const onMainContactIdChanged = async (toggleFromRelatedContactId: ModelId) => {
        const mainContactId: ModelId | null = editingCompany.mainContactId === toggleFromRelatedContactId ? null : toggleFromRelatedContactId;
        update({
            mainContactId
        });
        const response = await saveModel(AppModels.companies, { id: editingCompany.id, mainContactId });
        update({
            mainContactId: response.mainContactId
        });
    };

    if (!storedCompany) return <></>;

    const onCompanyImageUpload = async (fileInfo: FileInfo) => {
        await saveModel(AppModels.companies, {
            id: storedCompany?.id,
            imageFileId: fileInfo
        });
    };

    return <Container>
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <Grid container sx={{ display: 'flex', flexDirection: 'row' }}>
                    <Grid item sx={{ width: 100, height: 100, display: 'flex', position: 'relative', alignItems: 'center' }}>
                        {storedCompany.imageFileId ? <FileLoader id={storedCompany.imageFileId} render={file => <Avatar sx={{ width: '100%', height: '100%' }} src={file.url} />} /> : <Avatar sx={{ width: '100%', height: '100%' }}><Person /></Avatar>}
                        <div className="appearOnParentHover" style={{ textAlign: 'center', position: 'absolute', bottom: 2, width: '100%' }}>
                            <AWSImageButtonUploader onUpload={onCompanyImageUpload} variant='contained' size='small' />
                        </div>
                    </Grid>
                    <Grid item sx={{ flexGrow: 1, p: 2 }}>
                        <Typography variant='h3'>{storedCompany.name}</Typography>
                        <Typography variant='subtitle1'>{notEmptyString(storedCompany.type) ?? 'Compagnie / entreprise'}</Typography>
                    </Grid>
                </Grid>

            </Grid>
            <Grid item xs={12} sm={6} lg={6} bgcolor='white'>
                <Grid container spacing={2} columns={2}>
                    <Grid item xs={2}>
                        <Typography gutterBottom variant='h4'>Informations</Typography>
                    </Grid>

                    <Grid item xs={2} md={1}>
                        <TextField
                            fullWidth
                            label='Nom de la compagnie'
                            required
                            value={editingCompany.name}
                            error={!!editingCompany.__errors?.name}
                            helperText={editingCompany.__errors?.name}
                            onChange={(event) => update({ name: event.target.value })} />
                    </Grid>
                    <Grid item xs={2} md={1}>
                        <TextField
                            fullWidth
                            label='Code'
                            placeholder={suggestCode}
                            value={editingCompany.code}
                            error={!!editingCompany.__errors?.code}
                            helperText={editingCompany.__errors?.code}
                            onChange={(event) => update({ code: event.target.value })} />
                    </Grid>
                    <Grid item xs={2}>
                        <SuperSearchInput
                            textfieldProps={{ label: 'Marque / bannière' }}
                            filter={[AppModels.brands]}
                            value={editingCompany.brandId ? { type: AppModels.brands, id: editingCompany.brandId } : undefined}
                            onItemSelected={(_type: AppModels, id: ModelId) => {
                                update({ brandId: id });
                            }}
                            onCreate={onCreateBrand}
                            onSetEmpty={() => {
                                update({ brandId: null });
                            }}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <TextField
                            fullWidth
                            label="Type d'entreprise / produits et services"
                            required
                            value={editingCompany.type}
                            error={!!editingCompany.__errors?.type}
                            helperText={editingCompany.__errors?.type}
                            onChange={(event) => update({ type: event.target.value })} />
                    </Grid>
                    <Grid item xs={2}>
                        <TextField
                            fullWidth
                            label='Site Internet'
                            value={editingCompany.website}
                            error={!!editingCompany.__errors?.website}
                            helperText={editingCompany.__errors?.website}
                            onChange={(event) => update({ website: event.target.value })} />
                    </Grid>
                    <Grid item xs={2}>
                        <LocationInput
                            value={editingCompany.locationId}
                            onChange={(locationId: ModelId | null) => update({ locationId })}
                            onSetEmpty={() => { update({ locationId: null }); }} />
                    </Grid>
                    <Grid item xs={2} alignContent='center'>
                        <Button variant='contained' color='primary' onClick={onSaveClick}>Sauvegarder</Button>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} sm={6} bgcolor='white'>
                <Typography variant='h4'>Contacts</Typography>
                <RelatedContactList
                    type={AppModels.companies}
                    id={editingCompany.id}
                    mainContactId={editingCompany.mainContactId}
                    onMainCOntactToggle={onMainContactIdChanged} />
                <SuperSearchInput
                    filter={[AppModels.contacts]}
                    onItemSelected={(_type: AppModels, id: ModelId) => {
                        addContact(id);
                    }}
                    textfieldProps={{
                        size: 'small',
                        label: 'Ajouter un contact',
                        placeholder: 'Prénom et nom'
                    }}
                    onCreate={(name: string) => {
                        createContact(name);
                    }} />
            </Grid>
            <Grid item xs={12} sm={6} bgcolor='white'>
                <Typography variant='h4'>Projets</Typography>
                <List>
                    {projectsAsAClient.length === 0 ? <ListItem><ListItemText primary='Aucun' /></ListItem> : null}
                    {projectsAsAClient.map(project => <ListItem>
                        <ListItemButton component={Link} to={'/project/' + project.id}>
                            <ListItemText
                                primary={project.name}
                                secondary={project.code}
                            />
                        </ListItemButton>
                    </ListItem>

                    )}
                </List>
            </Grid>
            {editingCompany.brandId ? <Grid item xs={12} sm={6} bgcolor='white'>
                <Typography variant='h4'>Produits</Typography>
                <BrandProductList brandId={editingCompany.brandId} />
            </Grid> : null}
        </Grid>
    </Container>;
}; 
